import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { safetyMargins } from '@bedrock-rdk/constants';
import Grid from '../../tornado/templates/blocks/grid/grid.component';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../../tornado/utils/sizes';
import { PORTION_REQUESTED_SIGNAL, SELECTED_SIGNAL } from '../../tornado/signals';
import { PLACEHOLDER } from '../../tornado/utils/virtualization';
import { fetchBlockPage } from '../../../logic/layout/layout.actions';
import { HIDE_POPUP_SIGNAL } from '../../popupContainer/popupContainer.constants';
import { createItemAction } from '../../cta/cta.actions';
import { defaultContext, getInitialStyle } from './gridPopup.style';
import connect from './gridPopup.connect';

export class GridPopup extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        passSignals: {
          [HIDE_POPUP_SIGNAL]: true,
        },
        Grid: {
          type: Grid,
          signals: {
            [PORTION_REQUESTED_SIGNAL]: '_handlePortionRequest',
            [SELECTED_SIGNAL]: '_handleSelected',
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._blockIndex = undefined;
  }

  _active() {
    this._unsubscribe = connect(this);
  }

  _inactive() {
    if (this._unsubscribe) {
      this._unsubscribe();
    }
    this._layout = undefined;
    this._block = undefined;
  }

  set layout(value) {
    if (!value) {
      return;
    }

    this._layout = value;
    this._renderGrid();
  }

  set block(value) {
    if (!value) {
      return;
    }

    this._block = value;
    this._renderGrid();
  }

  _renderGrid() {
    if (!this._layout || !this._block || !Number.isFinite(this._block.blockIndex)) {
      return;
    }

    const { screenSize } = this._style;
    const view = this.tag('Grid');
    const block = this._layout.blocks[this._block.blockIndex];
    const w = SCREEN_WIDTH[screenSize] - safetyMargins.horizontal[screenSize];
    const h = SCREEN_HEIGHT[screenSize];

    if (view.data) {
      view.data = { ...this._block, rowSize: { w, h }, blockIndex: 0, block, showAllItems: true };

      return;
    }

    const { itemSize } = this._block;
    const blockSize = Grid.getOffsetsByBlockData({ data: block, blockIndex: 0, showAllItems: true });
    const rowsCount = Grid.getRowCount({ content: block.content, showAllItems: true }, screenSize);
    const gap = Grid.getContentGap(screenSize);

    const rowSize = itemSize.h + gap;
    const halfOfScreenHeight = Math.round(h / 2);
    const halfOfRowSize = Math.round(rowSize / 2);
    let offset = 0;

    this._positions = [];
    this._index = 0;

    for (let i = 0; i < rowsCount; i += 1) {
      this._positions.push(Math.min(halfOfScreenHeight - halfOfRowSize - blockSize.top - offset, 0));
      offset += rowSize;
    }

    if (this._positions.length && offset > h) {
      const minOffset = h - offset - safetyMargins.vertical[screenSize] - blockSize.top - gap;
      this._positions = this._positions.map(y => Math.max(minOffset, y));
    }

    view.data = { ...this._block, rowSize: { w, h }, block, blockIndex: 0, showAllItems: true };
  }

  _handlePortionRequest(portion) {
    Store.dispatch(fetchBlockPage(portion));
  }

  _handleSelected(item) {
    if (item && item.id && item.id !== PLACEHOLDER.id) {
      this.signal(HIDE_POPUP_SIGNAL);
      Store.dispatch(createItemAction({ item }));
    }
  }

  _setIndex(index = this._index) {
    const position = this._positions[index];

    if (!Number.isFinite(position)) {
      return;
    }

    this._index = index;
    this.tag('Grid').setSmooth('y', position);
  }

  _getFocused() {
    return this.tag('Grid');
  }

  _handleUp() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);
    }

    return true;
  }

  _handleDown() {
    if (this._index < this._positions.length - 1) {
      this._setIndex(this._index + 1);
    }

    return true;
  }
}
